import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  output,
  signal,
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {uniqId} from '@matchsource/utils';

@Component({
  selector: 'ms-radio-button',
  templateUrl: './radio-button.component.html',
  styleUrls: ['./radio-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioButtonComponent),
      multi: true,
    },
  ],
})
export class RadioButtonComponent implements ControlValueAccessor {
  readonly value = input<unknown>();
  readonly innerHtmlContent = input<string | null>(null);
  readonly inputId = input(uniqId('rad_'));
  readonly label = input('');
  readonly readOnly = input(false);
  readonly isUncheckAllowed = input(false);
  readonly disabled = input(false, {transform: booleanAttribute});
  // Set programmatically via ControlValueAccessor (attachToGroup/setDisabled/setDisabledState)
  // when the radio button is part of an `ms-radio-group`.
  private readonly controlDisabled = signal(false);
  readonly isDisabled = computed(() => this.disabled() || this.controlDisabled());

  readonly name = signal<string | undefined>(undefined);
  readonly selected = signal(false);

  readonly groupSelectionChange$ = output<unknown | null>();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (val: unknown | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouch: () => void = () => {};

  attachToGroup(group: {value: unknown; disabled: boolean}, name: string): void {
    this.name.set(name);
    this.setDisabled(group.disabled);
    this.setSelected(group.value === this.value());
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  detachFromGroup(): void {}

  setName(name: string): void {
    this.name.set(name);
  }

  setDisabled(isDisabled: boolean): void {
    this.controlDisabled.set(isDisabled);
  }

  setSelected(isSelected: boolean): void {
    this.selected.set(!!isSelected);
  }

  click(event: Event): void {
    event.stopPropagation();
    if (this.isDisabled()) {
      return;
    }
    if (!this.isUncheckAllowed() && this.selected()) {
      return;
    }

    if (this.isInGroupMode()) {
      const next = this.selected() ? null : this.value();
      this.groupSelectionChange$.emit(next);
    } else {
      const nextSelected = !this.selected();
      this.selected.set(nextSelected);
      this.onChange(nextSelected ? this.value() : null);
      this.onTouch();
    }
  }

  isInGroupMode(): boolean {
    return !!this.name();
  }

  blur() {
    if (this.onTouch) {
      this.onTouch();
    }
  }

  focusin(event: Event): void {
    event.stopPropagation();
  }

  writeValue(obj: unknown): void {
    this.selected.set(obj === this.value());
  }

  registerOnChange(fn: (val: unknown | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.controlDisabled.set(isDisabled);
  }
}
